import React from 'react';
import { notifyTicketCreated } from '../../../../../actions/ui';
import { getCustomer } from '../../../../../helpers/customer-helper';
import { uploadFileAsset } from '../../../../../helpers/file-helper';
import { getProject, getProjectCustomFields } from '../../../../../helpers/project-helper';
import {
    createTicket,
    getTicketCategories,
    getTicketSubcategories,
} from '../../../../../helpers/ticket-helper';
import { getUserList } from '../../../../../helpers/user-helper';
import { getGroup } from '../../../../../helpers/group-helper';

export const ADDITIONAL_FIELD_TYPES = {
    SHORT_TEXT: 'SHORT_TEXT',
    LONG_TEXT: 'LONG_TEXT',
    NUMBER: 'NUMBER',
    LIST: 'LIST',
    CHECKBOX: 'CHECKBOX',
};

const parseCustomFieldValue = (field, rawValue) => {
    const fieldType = field?.type;

    if (fieldType === ADDITIONAL_FIELD_TYPES.NUMBER) {
        if (rawValue === '' || rawValue === null || rawValue === undefined) {
            return undefined;
        }

        const parsed = Number(rawValue);
        return Number.isNaN(parsed) ? undefined : parsed;
    }

    if (fieldType === ADDITIONAL_FIELD_TYPES.CHECKBOX) {
        return Boolean(rawValue);
    }

    const normalized = String(rawValue ?? '').trim();
    return normalized || undefined;
};

const getPlainTextFromHtml = (html = '') => {
    return String(html || '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
};

const getProjectCustomerId = (project) => {
    if (!project?.customer) {
        return '';
    }

    if (typeof project.customer === 'object') {
        return String(project.customer?._id || '');
    }

    return String(project.customer);
};

const normalizeTag = (value = '') => {
    return String(value || '').trim().replace(/\s+/g, ' ');
};

const parseTags = (value = '') => {
    const items = String(value || '')
        .split(',')
        .map((tag) => normalizeTag(tag))
        .filter(Boolean);

    return Array.from(new Set(items));
};

export const useTaskCreateForm = ({
    token,
    authUserId,
    dispatch,
    navigate,
    asModal,
    onClose,
    onCreated,
}) => {
    const [activeTab, setActiveTab] = React.useState('general');

    const [isSaving, setIsSaving] = React.useState(false);
    const [isLoadingLookups, setIsLoadingLookups] = React.useState(true);
    const [isLoadingAdditionalFields, setIsLoadingAdditionalFields] = React.useState(false);
    const [error, setError] = React.useState('');

    const [customers, setCustomers] = React.useState([]);
    const [allProjects, setAllProjects] = React.useState([]);
    const [categories, setCategories] = React.useState([]);
    const [subcategories, setSubcategories] = React.useState([]);
    const [additionalFieldDefs, setAdditionalFieldDefs] = React.useState([]);
    const [additionalFieldSections, setAdditionalFieldSections] = React.useState([]);
    const [additionalFieldValues, setAdditionalFieldValues] = React.useState({});
    const [tagInput, setTagInput] = React.useState('');

    const [users, setUsers] = React.useState([]);
    const [groups, setGroups] = React.useState([]);

    const [form, setForm] = React.useState({
        title: '',
        description: '',
        type: 'INCIDENT',
        priority: 'MEDIUM',
        impact: 'LOW',
        urgency: 'LOW',
        source: 'PORTAL',
        customer: '',
        project: '',
        category: '',
        subcategory: '',
        tags: '',
        attachments: [],
        requester: '',
        assignee: '',
        group: '',
    });

    const selectedCustomerLabel = React.useMemo(() => {
        const selected = customers.find((customer) => String(customer._id) === String(form.customer));
        return selected ? `${selected.code} - ${selected.name}` : 'Sin cliente';
    }, [customers, form.customer]);

    const selectedProjectLabel = React.useMemo(() => {
        const selected = allProjects.find((project) => String(project._id) === String(form.project));
        return selected ? `${selected.code} - ${selected.name}` : 'Sin proyecto';
    }, [allProjects, form.project]);

    const availableProjects = React.useMemo(() => {
        if (!form.customer) {
            return [];
        }

        return allProjects.filter((project) => getProjectCustomerId(project) === String(form.customer));
    }, [allProjects, form.customer]);

    const tagBadges = React.useMemo(() => parseTags(form.tags), [form.tags]);

    const syncTagBadges = React.useCallback((nextTags) => {
        const uniqueTags = Array.from(new Set((Array.isArray(nextTags) ? nextTags : []).map((tag) => normalizeTag(tag)).filter(Boolean)));
        setForm((previous) => ({
            ...previous,
            tags: uniqueTags.join(', '),
        }));
    }, []);

    const handleAddTag = React.useCallback(() => {
        const nextTag = normalizeTag(tagInput);
        if (!nextTag) {
            return;
        }

        syncTagBadges([...tagBadges, nextTag]);
        setTagInput('');
    }, [tagBadges, tagInput, syncTagBadges]);

    const handleRemoveTag = React.useCallback((tagToRemove) => {
        syncTagBadges(tagBadges.filter((tag) => tag !== tagToRemove));
    }, [tagBadges, syncTagBadges]);

    React.useEffect(() => {
        const loadLookups = async () => {
            if (!token) {
                setIsLoadingLookups(false);
                return;
            }

            setIsLoadingLookups(true);
            setError('');

            const [customersResp, projectsResp] = await Promise.all([
                getCustomer(token, { limit: 100, offset: 0, status: 'ACTIVE' }),
                getProject(token, { limit: 100, offset: 0, status: 'ACTIVE' }),
            ]);

            if (!customersResp?.success || !projectsResp?.success) {
                setError(customersResp?.message || projectsResp?.message || 'No se pudieron cargar catálogos.');
                setCustomers([]);
                setAllProjects([]);
                setIsLoadingLookups(false);
                return;
            }

            const customerRows = Array.isArray(customersResp.data) ? customersResp.data : [];
            const projectRows = Array.isArray(projectsResp.data) ? projectsResp.data : [];

             const [usersResp, groupsResp] = await Promise.all([
                getUserList(token, { limit: 200, offset: 0, status: 'ACTIVE' }),
                getGroup(token, { limit: 200, offset: 0, status: 'ACTIVE' }),
            ]);
    
            setUsers(Array.isArray(usersResp?.data) ? usersResp.data : []);
            setGroups(Array.isArray(groupsResp?.data) ? groupsResp.data : []); 

            setCustomers(customerRows);
            setAllProjects(projectRows);

            setIsLoadingLookups(false);
        };

        loadLookups();
    }, [token]);

    React.useEffect(() => {
        if (!form.customer) {
            setForm((previous) => {
                if (!previous.project) {
                    return previous;
                }

                return {
                    ...previous,
                    project: '',
                    category: '',
                    subcategory: '',
                };
            });
            return;
        }

        const isCurrentProjectValid = availableProjects.some((project) => String(project._id) === String(form.project));
        if (isCurrentProjectValid) {
            return;
        }

        setForm((previous) => ({
            ...previous,
            project: '',
            category: '',
            subcategory: '',
        }));
    }, [availableProjects, form.customer, form.project]);

    React.useEffect(() => {
        const loadCategories = async () => {
            if (!token || !form.project) {
                setCategories([]);
                setSubcategories([]);
                setForm((previous) => ({ ...previous, category: '', subcategory: '' }));
                return;
            }

            const categoriesResp = await getTicketCategories(token, form.project);
            if (!categoriesResp?.success) {
                setCategories([]);
                setSubcategories([]);
                setForm((previous) => ({ ...previous, category: '', subcategory: '' }));
                return;
            }

            const categoryRows = Array.isArray(categoriesResp.data) ? categoriesResp.data : [];
            const nextCategoryId = categoryRows[0]?._id || '';

            setCategories(categoryRows);
            setForm((previous) => ({
                ...previous,
                category: previous.category && categoryRows.some((row) => row._id === previous.category)
                    ? previous.category
                    : nextCategoryId,
                subcategory: '',
            }));
        };

        loadCategories();
    }, [token, form.project]);

    React.useEffect(() => {
        const loadSubcategories = async () => {
            if (!token || !form.project) {
                setSubcategories([]);
                return;
            }

            const params = form.category ? { category: form.category } : {};
            const subcategoriesResp = await getTicketSubcategories(token, form.project, params);

            if (!subcategoriesResp?.success) {
                setSubcategories([]);
                setForm((previous) => ({ ...previous, subcategory: '' }));
                return;
            }

            const rows = Array.isArray(subcategoriesResp.data) ? subcategoriesResp.data : [];
            setSubcategories(rows);
            setForm((previous) => ({
                ...previous,
                subcategory: previous.subcategory && rows.some((row) => row._id === previous.subcategory)
                    ? previous.subcategory
                    : (rows[0]?._id || ''),
            }));
        };

        loadSubcategories();
    }, [token, form.project, form.category]);

    React.useEffect(() => {
        const loadAdditionalFields = async () => {
            if (!token || !form.project) {
                setAdditionalFieldDefs([]);
                setAdditionalFieldSections([]);
                setAdditionalFieldValues({});
                return;
            }

            setIsLoadingAdditionalFields(true);

            const resp = await getProjectCustomFields(token, form.project);

            if (!resp?.success) {
                setAdditionalFieldDefs([]);
                setAdditionalFieldSections([]);
                setAdditionalFieldValues({});
                setIsLoadingAdditionalFields(false);
                return;
            }

            const rawFields = Array.isArray(resp?.data?.customFields)
                ? resp.data.customFields
                : (Array.isArray(resp?.data) ? resp.data : []);
            const customFieldGroups = Array.isArray(resp?.data?.customFieldGroups)
                ? resp.data.customFieldGroups
                : [];

            const normalizedFields = rawFields.filter((field) => String(field?.scope || 'TICKET').toUpperCase() === 'TICKET');

            const groupNameById = customFieldGroups.reduce((accumulator, groupItem) => {
                if (groupItem?._id) {
                    accumulator[groupItem._id] = groupItem.name || 'General';
                }
                return accumulator;
            }, {});

            const grouped = {};

            normalizedFields.forEach((field) => {
                const fieldKey = field?.key || field?._id;
                if (!fieldKey) {
                    return;
                }

                const fieldGroupValue = field?.fieldGroup;
                const groupId = typeof fieldGroupValue === 'object' ? fieldGroupValue?._id : fieldGroupValue;
                const groupNameFromDef = typeof fieldGroupValue === 'object' ? fieldGroupValue?.name : '';
                const groupName = groupNameFromDef || groupNameById[groupId] || 'Otros';

                if (!grouped[groupName]) {
                    grouped[groupName] = [];
                }

                grouped[groupName].push(field);
            });

            const sections = Object.entries(grouped).map(([groupName, fields], index) => ({
                key: `group-${index}`,
                label: groupName,
                fields,
            }));

            setAdditionalFieldDefs(normalizedFields);
            setAdditionalFieldSections(sections);
            setAdditionalFieldValues((previous) => {
                const nextValues = {};
                normalizedFields.forEach((field) => {
                    const key = field.key || field._id;
                    if (!key) {
                        return;
                    }

                    if (Object.prototype.hasOwnProperty.call(previous, key)) {
                        nextValues[key] = previous[key];
                        return;
                    }

                    if (field.type === ADDITIONAL_FIELD_TYPES.CHECKBOX) {
                        nextValues[key] = false;
                        return;
                    }

                    nextValues[key] = '';
                });
                return nextValues;
            });

            setIsLoadingAdditionalFields(false);
        };

        loadAdditionalFields();
    }, [token, form.project]);

    const handleInputChange = ({ target }) => {
        const { name, value } = target;

        if (name === 'customer') {
            setForm((previous) => ({
                ...previous,
                customer: value,
                project: '',
                category: '',
                subcategory: '',
            }));
            return;
        }

        setForm((previous) => ({
            ...previous,
            [name]: value,
        }));
    };

    const handleFormSelectChange = React.useCallback((name, value) => {
        if (name === 'customer') {
            setForm((previous) => ({
                ...previous,
                customer: value,
                project: '',
                category: '',
                subcategory: '',
            }));
            return;
        }

        setForm((previous) => ({
            ...previous,
            [name]: value,
        }));
    }, []);

    const handleAttachmentsChange = ({ target }) => {
        const files = Array.from(target.files || []);
        setForm((previous) => ({
            ...previous,
            attachments: files,
        }));
    };

    const handleAdditionalFieldChange = ({ target }, fieldKey) => {
        const { type, checked, value } = target;
        setAdditionalFieldValues((previous) => ({
            ...previous,
            [fieldKey]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleAdditionalFieldSelectChange = React.useCallback((fieldKey, nextValue) => {
        setAdditionalFieldValues((previous) => ({
            ...previous,
            [fieldKey]: nextValue,
        }));
    }, []);

    const handleDescriptionHtmlChange = (event) => {
        const htmlValue = event.currentTarget?.innerHTML || '';
        setForm((previous) => ({
            ...previous,
            description: htmlValue,
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!token || isSaving) {
            return;
        }

        const payload = {
            title: String(form.title || '').trim(),
            description: String(form.description || '').trim(),
            type: form.type,
            priority: form.priority,
            impact: form.impact,
            urgency: form.urgency,
            source: form.source,
            requester: form.requester || authUserId || undefined,
            assignee: form.assignee || undefined,
            group: form.group || undefined,
            customer: form.customer || undefined,
            project: form.project || undefined,
            category: form.category || undefined,
            subcategory: form.subcategory || undefined,
            tags: tagBadges,
            customFields: {},
        };

        if (!payload.title || !getPlainTextFromHtml(payload.description)) {
            setError('Completa título y descripción para crear el ticket.');
            return;
        }

        if (!payload.customer || !payload.project) {
            setError('Debes seleccionar cliente y proyecto para crear el ticket.');
            return;
        }

        const selectedProject = allProjects.find((project) => String(project._id) === String(payload.project));
        if (!selectedProject || getProjectCustomerId(selectedProject) !== String(payload.customer)) {
            setError('El proyecto seleccionado no corresponde al cliente elegido.');
            return;
        }

        for (let index = 0; index < additionalFieldDefs.length; index += 1) {
            const field = additionalFieldDefs[index];
            const key = field.key || field._id;
            if (!key) {
                continue;
            }

            const mappedValue = parseCustomFieldValue(field, additionalFieldValues[key]);

            if (field.required && (mappedValue === undefined || mappedValue === '')) {
                setError(`Completa el campo adicional requerido: ${field.name || key}.`);
                setActiveTab(additionalFieldSections[0]?.key ? `additional-${additionalFieldSections[0].key}` : 'general');
                return;
            }

            if (mappedValue !== undefined) {
                payload.customFields[key] = mappedValue;
            }
        }

        if (Object.keys(payload.customFields).length === 0) {
            delete payload.customFields;
        }

        setIsSaving(true);
        setError('');

        const resp = await createTicket(token, payload);

        if (!resp?.success) {
            setError(resp?.message || 'No se pudo crear el ticket.');
            setIsSaving(false);
            return;
        }

        const createdTicket = resp?.data;
        const createdTicketId = createdTicket?._id;

        if (createdTicketId && Array.isArray(form.attachments) && form.attachments.length > 0) {
            await Promise.all(form.attachments.map((file) => {
                return uploadFileAsset(token, {
                    file,
                    purpose: 'TICKET_ATTACHMENT',
                    tags: ['ticket', createdTicket?.code || createdTicketId],
                    referenceType: 'TICKET',
                    referenceId: createdTicketId,
                    referenceField: 'attachments',
                    referenceLabel: payload.title,
                    isPublic: false,
                });
            }));
        }

        if (createdTicketId) {
            dispatch(notifyTicketCreated());

            if (asModal) {
                if (typeof onCreated === 'function') {
                    onCreated(createdTicket);
                }
                if (typeof onClose === 'function') {
                    onClose();
                }
                navigate(`/tasks/detail/${createdTicketId}`, {
                    state: { ticketCode: createdTicket?.code || '' },
                });
                return;
            }

            navigate(`/tasks/detail/${createdTicketId}`, {
                state: { ticketCode: createdTicket?.code || '' },
            });
            return;
        }

        if (asModal && typeof onClose === 'function') {
            onClose();
            return;
        }

        navigate('/tasks');
    };

    const handleCancel = () => {
        if (asModal && typeof onClose === 'function') {
            onClose();
            return;
        }

        navigate('/tasks');
    };

    return {
        activeTab,
        setActiveTab,
        isSaving,
        isLoadingLookups,
        isLoadingAdditionalFields,
        error,
        customers,
        categories,
        subcategories,
        additionalFieldDefs,
        additionalFieldSections,
        additionalFieldValues,
        tagInput,
        form,
        availableProjects,
        selectedCustomerLabel,
        selectedProjectLabel,
        tagBadges,
        users,
        groups,
        handleAddTag,
        handleRemoveTag,
        handleInputChange,
        handleFormSelectChange,
        handleAttachmentsChange,
        handleAdditionalFieldChange,
        handleAdditionalFieldSelectChange,
        handleDescriptionHtmlChange,
        handleSubmit,
        handleCancel,
        setTagInput,
    };
};